import cn from 'classnames';
import React, { useRef, useState } from 'react';
import { Button } from '../button';
import { idGenerator } from '../common/Utils';
import { TooltipPosition, TooltipSize } from '../tooltip';
import './BasicFile.css';
import Label from './Label';

interface Props {
  id?: string;
  name?: string;
  value?: File;
  label?: string;
  required?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  btnLabel?: string;
  placeholder?: string;
  helpText?: string | JSX.Element;
  helpTextPosition?: 'after-label' | 'below-field';
  helperTextToolTipConfig?: {
    size?: TooltipSize;
    position?: TooltipPosition;
  };
  error?: boolean;
  errorMessage?: string;
  attrs?: React.InputHTMLAttributes<HTMLInputElement>;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onReset?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const BasicFile: React.FC<Props> = props => {
  const {
    id,
    disabled,
    error,
    errorMessage,
    helpText,
    helpTextPosition = 'below-field',
    helperTextToolTipConfig,
    label,
    required,
    hidden,
    name,
    onBlur,
    onChange,
    onReset,
    placeholder,
    btnLabel,
    value,
    attrs
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  const [uid] = useState(idGenerator(id));

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.keyCode === 13 || e.keyCode === 32) {
      inputRef.current?.click();
    }
  };

  const onResetHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (onReset)
      onReset(e);
  };

  return (
    <div
      className={cn('clr-form-control', {
        'clr-form-control-disabled': disabled
      })}
      hidden={hidden}>
      {label && (
        <Label
          label={label}
          required={required}
          enableHelpText={helpTextPosition === 'after-label'}
          helpText={helpText}
          tooltipPosition={
            helperTextToolTipConfig
              ? helperTextToolTipConfig.position
              : undefined
          }
          tooltipSize={
            helperTextToolTipConfig ? helperTextToolTipConfig.size : undefined
          }
          htmlFor={uid}
        />
      )}
      <div className={cn('clr-control-container', { 'clr-error': error })}>
        <div className="clr-file-wrapper">
          <label
            htmlFor={uid}
            className="clr-control-label"
            tabIndex={0}
            onKeyUp={handleKeyUp}
          >
            <span className="btn btn-sm">{btnLabel || 'browse'}</span>
            {value && (
              <>
                <span className="crc-file-name">{value.name}</span>
                <Button size="sm" family="link" icon={true} onClick={onResetHandler}>
                  <clr-icon shape="times" />
                </Button>
              </>
            )}
          </label>
          <input
            id={uid}
            className="clr-file"
            type="file"
            disabled={disabled}
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            ref={inputRef}
            tabIndex={-1}
            aria-describedby={`${uid}-subText`}
            aria-invalid={error}
            title={label === undefined ? placeholder || name : undefined}
            {...attrs}
          />
        </div>
        <span className="clr-subtext" id={`${uid}-subText`}>
          {error ? (
            <>
              <clr-icon class="clr-validate-icon" shape="exclamation-circle" />
              {errorMessage}
            </>
          ) : (
            <>{helpTextPosition === 'below-field' && helpText}</>
          )}
        </span>
      </div>
    </div>
  );
};
